'use client';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@gartner-digital-markets/nimbus-v3-ui';
import { useFormContext } from 'react-hook-form';

interface ParsedSelectOption {
  label: string;
  value: string;
}

interface SelectFieldProps {
  name: string;
  label: string;
  options: readonly string[];
  placeholder?: string;
  required?: boolean;
  /** When true, the inline validation message is not rendered as part of
   *  this component — callers can render it separately to keep error
   *  text out of flex/grid alignment between the select and a sibling button. */
  hideMessage?: boolean;
  onOpen?: () => void;
}

export const parseSelectOption = (option: string): ParsedSelectOption => {
  const separatorIndex = option.indexOf('|');

  if (separatorIndex === -1) {
    return { label: option, value: option };
  }

  const label = option.slice(0, separatorIndex).trim();
  const value = option.slice(separatorIndex + 1).trim();

  if (!(label && value)) {
    return { label: option, value: option };
  }

  return { label, value };
};

export const SelectField = ({
  name,
  label,
  options,
  placeholder = 'Select an option',
  required = true,
  hideMessage = false,
  onOpen,
}: SelectFieldProps) => {
  const { control, getFieldState, trigger } = useFormContext();
  const parsedOptions = options.map(parseSelectOption);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const fieldState = getFieldState(name);
        const hasValue = !!field.value;
        const showSuccess =
          fieldState.isTouched && hasValue && !fieldState.invalid;
        const hasError = !!fieldState.error;

        return (
          <FormItem>
            {/* Keep label neutral even when the field is in error state.
                Nimbus FormLabel defaults to text-nimbus-destructive on
                error, which made the bolded compare-page question read
                as an alarm rather than a question. The red trigger
                border + error message already signal the error state. */}
            <FormLabel className="mb-1 block flex items-center gap-1 text-base leading-6 font-bold text-nimbus-foreground">
              {label}
              {required && (
                <span aria-hidden={true} className="text-nimbus-destructive">
                  *
                </span>
              )}
            </FormLabel>
            <FormControl>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setTimeout(() => trigger(name), 0);
                }}
                onOpenChange={(open) => {
                  if (open) {
                    onOpen?.();
                  } else {
                    field.onBlur();
                    trigger(name);
                  }
                }}
              >
                {/* Force the SelectValue span to truncate. Nimbus already
                    applies similar utilities via `*:data-[slot=select-value]:*`,
                    but in Tailwind 3 that variant compiles to
                    `.foo[data-slot="select-value"] > *` (the attribute is read
                    on the trigger, which has `select-trigger`), so the rules
                    never reach the value. The `[&_[data-slot=...]]:` arbitrary
                    descendant variant compiles correctly. Without this the
                    value's intrinsic min-content (full option text on one
                    line) propagates up the parent chain and the modal
                    overflows on narrow viewports. */}
                <SelectTrigger
                  error={hasError}
                  success={showSuccess}
                  data-testid="bx-forms-select-trigger"
                  className="[&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:truncate [&_[data-slot=select-value]]:min-w-0"
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {parsedOptions.map((option, index) => (
                    <SelectItem
                      key={`${option.value}-${index}`}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            {hideMessage ? null : <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
};
